import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/AuthLayout';
import { OtpForm } from '../components/OtpForm';
import { useAuthStore } from '../stores/auth.store';
import { hydrateUserProfile, useEnsureUserProfile } from '../hooks/useUserProfile';
import { useLogout } from '../hooks/useLogout';
import { routePaths } from '@/app/routes';
import { FullScreenLoader } from '@/components/feedback/FullScreenLoader';

interface FromState {
  from?: { pathname?: string };
}

/**
 * Post-login email verification — the web counterpart of the mobile app's OTP
 * screen. `ProtectedRoute` sends every signed-in user whose profile says
 * `otp_verified: false` here (fresh registrations) until the code is confirmed.
 *
 * The auth server mails the code during the hosted login; verify and resend go
 * straight to the auth server with the session's Bearer (provider "main"), so
 * the website proxy is not involved.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useLogout();

  // Direct visit without a loaded profile (e.g. hard refresh): fetch it.
  useEnsureUserProfile();

  const from = (location.state as FromState | null)?.from?.pathname ?? routePaths.home;

  if (user?.otp_verified !== false) {
    // Already verified (or no verification needed) — nothing to do here.
    return user ? <Navigate to={from} replace /> : <FullScreenLoader />;
  }

  const onVerified = async () => {
    // Re-read the profile so the route gate opens; if that fails, flip the
    // flag locally — the server has already accepted the code.
    const refreshed = await hydrateUserProfile();
    if (!refreshed) setUser({ ...user, otp_verified: true });
    navigate(from, { replace: true });
  };

  return (
    <AuthLayout>
      <header className="mb-6 text-center">
        <h1 className="text-lg font-bold text-ink">{t('verify_email.title')}</h1>
        <p className="mt-1 text-sm text-ink-mute">{t('verify_email.subtitle')}</p>
      </header>

      <OtpForm email={user.email} onVerified={() => void onVerified()} />

      <button
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="mt-7 w-full text-center text-sm text-ink-soft hover:underline disabled:opacity-50"
      >
        {t('verify_email.logout')}
      </button>
    </AuthLayout>
  );
}
